// A client for the `/api/` operations the ClojureScript app calls, for page scripts on
// server-rendered pages. `/api/` reads transit and answers JSON when asked to
// (`mf.response.api_response`), so requests are hand-encoded transit and responses are
// read as JSON — no transit reader ships to the browser. That JSON still wraps keyword,
// date, decimal and set values as `{"#K": …}`, `{"#D": …}` and so on
// (`mf.json.JSONApiEncoder`), which this client leaves undecoded.

export class Keyword {
  constructor(name) {
    this.name = name
  }
}

// Transit reserves a leading `~`, `^` or backtick on a string and escapes it with `~`.
function transitValue(value) {
  if (value instanceof Keyword) {
    return `~:${value.name}`
  }
  return typeof value === "string" && /^[~^`]/.test(value) ? `~${value}` : value
}

export function transitMap(entries) {
  return ["^ ", ...entries.flatMap(([key, value]) => [`~:${key}`, transitValue(value)])]
}

// `machtfit.api/auth-redirect-url`: an operation refused for its session names where to
// go, and a login gets the current path as `next`.
function authRedirectUrl(answer) {
  const location = window.top.location
  const path = `${location.pathname}${location.search}${location.hash}`
  for (const envelope of Object.values(answer?.result ?? {})) {
    const { name, redirect_url: url } = envelope?.error ?? {}
    if (url) {
      return name === "login-required" && !path.startsWith(url)
        ? `${url}${url.includes("?") ? "&" : "?"}next=${encodeURIComponent(path)}`
        : url
    }
  }
  return null
}

// The operation's own envelope, or null when the request itself failed. The ClojureScript
// callers split on the same line: an unreachable endpoint ran `:on-error`, while an
// operation that raised `ApiError` still ran `:on-success` with `success` false. A refused
// session navigates away instead, and the promise never settles, as no handler ran there.
export async function callOperation(operation, args, { csrfToken, alias = operation }) {
  let answer
  try {
    const response = await fetch("/api/", {
      method: "POST",
      headers: {
        "Content-Type": "application/transit+json",
        Accept: "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify([[`~:${operation}`, transitMap(args), `~:${alias}`]]),
    })
    if (!response.ok) {
      return null
    }
    answer = await response.json()
  } catch {
    return null
  }
  const redirect = authRedirectUrl(answer)
  if (redirect) {
    window.top.location.href = redirect
    return new Promise(() => {})
  }
  return answer?.result?.[alias] ?? { success: false }
}
